import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import hoursRecordService from '../../services/hoursRecordService';
import { useModules } from '../../context/LevelsContext';
import { Download, Clock, Loader2, FileText, ChevronDown } from 'lucide-react';

// ─── Helper ────────────────────────────────────────────
const formatHours = (hours) => {
    if (!hours || hours === 0) return '0h';
    if (hours < 0.1) return '< 0.1h';
    return `${hours.toFixed(1)}h`;
};

// ─── Inline icons (kept from original for form section) ─
const ClockIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
);

const FileIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
    </svg>
);

const LoadingSpinner = () => (
    <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-3 border-teal-500/30 border-t-teal-500 rounded-full animate-spin" />
    </div>
);

// ─── Sub-components ────────────────────────────────────
const FormField = ({ label, value, onChange, type = "text", required = false, placeholder = "" }) => (
    <div className="space-y-2">
        <label className="text-[11px] font-bold text-gray-500 dark:text-gray-500 uppercase tracking-widest ml-1">
            {label}{required && <span className="text-red-500 ml-1">*</span>}
        </label>
        <div className="bg-gray-50 dark:bg-[#1a1a1a] border border-gray-200 dark:border-white/5 rounded-2xl px-5 py-4 text-gray-900 dark:text-white font-medium focus-within:border-teal-500/50 transition-colors">
            <input
                type={type}
                value={value}
                onChange={e => onChange(e.target.value)}
                placeholder={placeholder}
                className="bg-transparent w-full outline-none placeholder-gray-400"
            />
        </div>
    </div>
);

const CategoryRow = ({ number, labelFr, labelEn, hours, language }) => (
    <div className="flex items-center justify-between bg-gray-50 dark:bg-[#1a1a1a] border border-gray-200 dark:border-white/5 rounded-xl px-5 py-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
            <span className="text-xs font-black text-gray-400 w-6 text-center shrink-0">{number}</span>
            <span className="text-sm text-gray-900 dark:text-white font-medium truncate">
                {language === 'fr' ? labelFr : labelEn}
            </span>
        </div>
        <span className="text-sm font-black text-teal-600 dark:text-teal-400 ml-4 whitespace-nowrap">
            {hours.toFixed(2)}h
        </span>
    </div>
);

// ─── Level Hours Card (like SchoolStats LevelSection) ──
const LevelHoursCard = ({ level, language }) => {
    const [open, setOpen] = useState(true);

    const label = language === 'fr' ? level.labelFr : level.labelEn;
    const bgColor = level.backgroundColor || '#6B7280';

    const handleDownload = (e) => {
        e.stopPropagation();
        const url = hoursRecordService.getSelfViewUrl(level.levelVar, language);
        window.open(url, '_blank');
    };

    return (
        <div className="bg-white dark:bg-[#121212] border border-gray-200 dark:border-white/5 rounded-2xl overflow-hidden shadow-sm dark:shadow-none">
            {/* Header — clickable toggle */}
            <button
                onClick={() => setOpen(!open)}
                className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-white/[0.02] transition"
            >
                <div className="flex items-center gap-3">
                    <span
                        className="px-3 py-1.5 rounded-lg text-xs font-black shadow-sm"
                        style={{ backgroundColor: bgColor, color: '#000' }}
                    >
                        {label}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-0' : '-rotate-90'}`} />
                </div>
                <div className="flex items-center gap-3">
                    <span className="flex items-center gap-1 text-xs font-bold text-amber-600 dark:text-amber-400">
                        <Clock className="w-3.5 h-3.5 text-amber-500 dark:text-amber-400" />
                        Total: {formatHours(level.totalHours)}
                    </span>
                    <button
                        onClick={handleDownload}
                        title={language === 'fr' ? 'Télécharger le relevé d\'heures' : 'Download hours record'}
                        className="p-1.5 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-500/20 transition"
                    >
                        <Download className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                    </button>
                </div>
            </button>

            {/* Expanded: categories grid */}
            {open && level.categories && level.categories.length > 0 && (
                <div className="border-t border-gray-100 dark:border-white/5">
                    <div className="px-5 py-3 flex items-center gap-2">
                        <Clock className="w-4 h-4 text-amber-500 dark:text-amber-400" />
                        <span className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                            {language === 'fr' ? 'Relevé d\'heures' : 'Hours Record'}
                        </span>
                    </div>
                    <div className="px-5 pb-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
                        {level.categories.map(cat => (
                            <div key={cat.number} className="flex items-center justify-between py-1">
                                <span className="text-xs text-gray-600 dark:text-gray-400 truncate pr-2">
                                    <span className="text-gray-400 dark:text-gray-500 mr-1">{cat.number}.</span>
                                    {language === 'fr' ? cat.labelFr : cat.labelEn}
                                </span>
                                <span className={`text-xs font-bold flex-shrink-0 ${
                                    cat.hours > 0
                                        ? 'text-amber-600 dark:text-amber-400'
                                        : 'text-gray-300 dark:text-gray-600'
                                }`}>
                                    {formatHours(cat.hours)}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── Main Component ────────────────────────────────────
const HoursRecord = ({ levelVar: fixedLevel, onClose } = {}) => {
    const { user } = useAuth();
    const { language } = useLanguage();
    const isModal = !!onClose;

    // All-levels overview state
    const [allLevelsData, setAllLevelsData] = useState(null);
    const [allLevelsLoading, setAllLevelsLoading] = useState(true);
    const [allLevelsError, setAllLevelsError] = useState(null);

    // Global history (all levels)
    const [allRecords, setAllRecords] = useState([]);

    // Per-level generate form state
    const [selectedLevel, setSelectedLevel] = useState(fixedLevel || '');
    const [hoursData, setHoursData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    // Form
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [medicalCertNumber, setMedicalCertNumber] = useState('');

    const { modules } = useModules();

    // All levels
    const allLevels = modules.filter(m => !m.isPack);

    // Level lookup helpers
    const getLevelColor = (code) => {
        const mod = modules.find(m => m.id === code);
        return mod?.newBackgroundColor || '#6B7280';
    };
    const getLevelLabel = (code) => {
        const mod = modules.find(m => m.id === code);
        return mod?.title || (code || '').toUpperCase();
    };

    // ─── Fetch all hours overview + global history on mount
    useEffect(() => {
        const fetchAll = async () => {
            setAllLevelsLoading(true);
            setAllLevelsError(null);
            try {
                const data = await hoursRecordService.getAllHours();
                setAllLevelsData(data);

                // Pre-fill name from API response
                if (data?.user) {
                    if (!firstName) setFirstName(data.user.firstName || '');
                    if (!lastName) setLastName(data.user.lastName || '');
                }

                // Auto-select first level for form if not fixed
                if (!fixedLevel && data?.levels?.length > 0 && !selectedLevel) {
                    setSelectedLevel(data.levels[0].levelVar);
                }
            } catch (err) {
                setAllLevelsError(language === 'fr'
                    ? 'Erreur lors du chargement des heures'
                    : 'Error loading hours data');
            } finally {
                setAllLevelsLoading(false);
            }
        };

        const fetchGlobalHistory = async () => {
            try {
                const records = await hoursRecordService.getHistory();
                setAllRecords(records);
            } catch (err) {
                console.error('Failed to fetch global hours record history:', err);
            }
        };

        fetchAll();
        fetchGlobalHistory();
    }, []);

    // ─── Auto-select first level if not set ────────────
    useEffect(() => {
        if (!fixedLevel && allLevels.length > 0 && !selectedLevel) {
            setSelectedLevel(allLevels[0].id);
        }
    }, [user]);

    // ─── Fetch per-level hours when level changes ──────
    useEffect(() => {
        if (!selectedLevel) return;

        const fetchHours = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await hoursRecordService.getHours(selectedLevel);
                setHoursData(data);

                // Pre-fill name from per-level data if not already set
                if (data?.user) {
                    if (!firstName) setFirstName(data.user.firstName || '');
                    if (!lastName) setLastName(data.user.lastName || '');
                }
            } catch (err) {
                setError(language === 'fr' ? 'Erreur lors du chargement des données' : 'Error loading data');
            } finally {
                setLoading(false);
            }
        };

        fetchHours();
    }, [selectedLevel]);

    // ─── Refresh global history helper ─────────────────
    const refreshGlobalHistory = async () => {
        try {
            const records = await hoursRecordService.getHistory();
            setAllRecords(records);
        } catch (err) {
            console.error('Failed to refresh global hours record history:', err);
        }
    };

    // ─── Form validation ───────────────────────────────
    const formValid = firstName.trim() && lastName.trim() && medicalCertNumber.trim();

    // ─── Generate handler ──────────────────────────────
    const handleGenerate = async () => {
        if (!formValid) return;

        setSubmitting(true);
        setError(null);
        try {
            const result = await hoursRecordService.generate({
                levelId: selectedLevel,
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                medicalCertNumber: medicalCertNumber.trim()
            });

            if (result.success && result.recordId) {
                const url = hoursRecordService.getViewUrl(result.recordId, language);
                window.open(url, '_blank');

                const updated = await hoursRecordService.getHours(selectedLevel);
                setHoursData(updated);

                await refreshGlobalHistory();
            }
        } catch (err) {
            setError(language === 'fr' ? 'Erreur lors de la génération' : 'Error generating record');
        } finally {
            setSubmitting(false);
        }
    };

    const handleViewRecord = (recordId) => {
        const url = hoursRecordService.getViewUrl(recordId, language);
        window.open(url, '_blank');
    };

    // ─── Translations ──────────────────────────────────
    const t = {
        title: language === 'fr' ? 'Relevé d\'heures' : 'Record of Hours',
        subtitle: language === 'fr'
            ? 'Consulte et génère ton relevé d\'heures de formation par catégorie Transport Canada'
            : 'View and generate your training hours record by Transport Canada category',
        overviewTitle: language === 'fr' ? 'Mes heures par niveau' : 'My hours by level',
        noHours: language === 'fr'
            ? 'Aucune heure enregistrée pour le moment. Commencez vos cours pour accumuler des heures !'
            : 'No hours recorded yet. Start your courses to accumulate hours!',
        generateTitle: language === 'fr' ? 'Générer un relevé officiel' : 'Generate an official record',
        selectLevel: language === 'fr' ? 'Sélectionner un niveau' : 'Select a level',
        hoursBreakdown: language === 'fr' ? 'Heures par catégorie' : 'Hours by Category',
        total: language === 'fr' ? 'Total' : 'Total',
        formTitle: language === 'fr' ? 'Mes informations' : 'My Information',
        firstName: language === 'fr' ? 'Prénom' : 'First Name',
        lastName: language === 'fr' ? 'Nom' : 'Last Name',
        medicalCert: language === 'fr' ? 'N° de licence ou de certificat médical' : 'Licence number or medical certificate number',
        generate: language === 'fr' ? 'Générer mon relevé' : 'Generate my record',
        generating: language === 'fr' ? 'Génération en cours...' : 'Generating...',
        historyTitle: language === 'fr' ? 'Relevés générés' : 'Generated Records',
        view: language === 'fr' ? 'Voir' : 'View',
    };

    // ─── Overview section (all levels with hours) ──────
    const overviewSection = (
        <div className="!mt-12 !mb-12">
            <div className="flex items-center gap-3 !mb-6">
                <div className="w-1.5 h-6 bg-amber-500 rounded-full" />
                <h2 className="text-lg font-black text-gray-900 dark:text-white">{t.overviewTitle}</h2>
            </div>

            {allLevelsLoading && <LoadingSpinner />}

            {allLevelsError && (
                <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-2xl p-4 text-red-700 dark:text-red-400 text-sm font-medium">
                    {allLevelsError}
                </div>
            )}

            {!allLevelsLoading && !allLevelsError && allLevelsData?.levels?.length === 0 && (
                <div className="bg-white dark:bg-[#121212] border border-gray-200 dark:border-white/5 rounded-2xl p-8 text-center">
                    <div className="mx-auto w-14 h-14 rounded-2xl bg-gray-100 dark:bg-white/5 flex items-center justify-center mb-4">
                        <Clock className="w-7 h-7 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t.noHours}</p>
                </div>
            )}

            {!allLevelsLoading && !allLevelsError && allLevelsData?.levels?.length > 0 && (
                <div className="space-y-4">
                    {allLevelsData.levels.map(level => (
                        <LevelHoursCard
                            key={level.levelVar}
                            level={level}
                            language={language}
                        />
                    ))}
                </div>
            )}
        </div>
    );

    // ─── Generate form section ─────────────────────────
    const generateSection = (
        <>
            <div className="flex items-center gap-3 !mb-6">
                <div className="w-1.5 h-6 bg-teal-500 rounded-full" />
                <h2 className="text-lg font-black text-gray-900 dark:text-white">{t.generateTitle}</h2>
            </div>

            {/* Level Selector */}
            <div className="bg-white dark:bg-[#121212] border border-gray-200 dark:border-white/5 rounded-[1.5rem] p-6 !mb-8 shadow-sm dark:shadow-none">
                <p className="text-[11px] font-bold text-gray-500 uppercase tracking-widest mb-4">
                    {language === 'fr' ? 'Niveaux disponibles' : 'Available Levels'}
                </p>
                <div className="flex flex-wrap gap-3">
                    {allLevels.map(level => (
                        <button
                            key={level.id}
                            onClick={() => setSelectedLevel(level.id)}
                            className={`rounded-2xl px-4 py-3 flex items-center gap-2 transition-all border-2 ${
                                selectedLevel === level.id
                                    ? 'border-gray-900 dark:border-white shadow-lg scale-[1.02]'
                                    : 'border-transparent opacity-70 hover:opacity-100 hover:scale-[1.02]'
                            }`}
                        >
                            <span
                                className="px-3 py-1.5 rounded-lg text-xs font-black shadow-sm flex-shrink-0"
                                style={{ backgroundColor: level.newBackgroundColor || getLevelColor(level.id), color: '#000' }}
                            >
                                {level.title || level.id.toUpperCase()}
                            </span>
                        </button>
                    ))}
                </div>
            </div>

            {/* Loading */}
            {loading && <LoadingSpinner />}

            {/* Error */}
            {error && (
                <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-2xl p-4 !mb-8 text-red-700 dark:text-red-400 text-sm font-medium">
                    {error}
                </div>
            )}

            {/* Hours Table & Form */}
            {hoursData && !loading && (
                <div className="grid grid-cols-1 lg:grid-cols-2 !gap-12">
                    {/* Left: Hours Breakdown */}
                    <div className={`${isModal ? '' : 'bg-white dark:bg-[#121212] border border-gray-200 dark:border-white/5 rounded-[2rem] shadow-sm dark:shadow-none'} p-8 space-y-4`}>
                        <div className="flex items-center gap-3 !mb-6">
                            <div className="w-1.5 h-6 bg-teal-500 rounded-full" />
                            <h3 className="text-lg font-black text-gray-900 dark:text-white">{t.hoursBreakdown}</h3>
                        </div>

                        <div className="space-y-2">
                            {(hoursData.categories || []).map(cat => (
                                <CategoryRow
                                    key={cat.number}
                                    number={cat.number}
                                    labelFr={cat.labelFr}
                                    labelEn={cat.labelEn}
                                    hours={cat.hours}
                                    language={language}
                                />
                            ))}
                        </div>

                        {/* Total row */}
                        <div className="flex items-center justify-between bg-teal-50 dark:bg-teal-500/10 border-2 border-teal-300 dark:border-teal-500/30 rounded-xl px-5 py-4 !mt-4">
                            <span className="text-sm font-black text-teal-800 dark:text-teal-300 uppercase tracking-wide">
                                {t.total}
                            </span>
                            <span className="text-lg font-black text-teal-700 dark:text-teal-300">
                                {(hoursData.totalHours || 0).toFixed(2)}h
                            </span>
                        </div>
                    </div>

                    {/* Right: Form + Generate */}
                    <div className="space-y-8">
                        <div className={`${isModal ? '' : 'bg-white dark:bg-[#121212] border border-gray-200 dark:border-white/5 rounded-[2rem] shadow-sm dark:shadow-none'} p-8 space-y-6`}>
                            <div className="flex items-center gap-3">
                                <div className="w-1.5 h-6 bg-teal-500 rounded-full" />
                                <h3 className="text-lg font-black text-gray-900 dark:text-white">{t.formTitle}</h3>
                            </div>

                            <div className="space-y-5">
                                <FormField
                                    label={t.firstName}
                                    value={firstName}
                                    onChange={setFirstName}
                                    required
                                />
                                <FormField
                                    label={t.lastName}
                                    value={lastName}
                                    onChange={setLastName}
                                    required
                                />
                                <FormField
                                    label={t.medicalCert}
                                    value={medicalCertNumber}
                                    onChange={setMedicalCertNumber}
                                    required
                                    placeholder="CA-XXXXXX"
                                />
                            </div>

                            <div className="pt-2">
                                <button
                                    onClick={handleGenerate}
                                    disabled={!formValid || submitting}
                                    className={`w-full py-4 rounded-full font-black text-sm shadow-xl transition-all ${
                                        formValid && !submitting
                                            ? 'bg-gradient-to-r from-teal-600 to-cyan-600 text-white shadow-teal-500/10 hover:scale-[1.02] active:scale-[0.98]'
                                            : 'bg-gray-200 dark:bg-white/5 text-gray-400 dark:text-gray-600 cursor-not-allowed shadow-none'
                                    }`}
                                >
                                    {submitting ? t.generating : t.generate}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </>
    );

    // ─── Global history section (all levels) ──────────
    const historySection = allRecords.length > 0 && (
        <div className="!mb-12">
            <div className="flex items-center gap-3 !mb-6">
                <div className="w-1.5 h-6 bg-teal-500 rounded-full" />
                <h2 className="text-lg font-black text-gray-900 dark:text-white">{t.historyTitle}</h2>
            </div>

            <div className={`${isModal ? '' : 'bg-white dark:bg-[#121212] border border-gray-200 dark:border-white/5 rounded-[2rem] shadow-sm dark:shadow-none'} p-6 md:p-8`}>
                <div className="space-y-3">
                    {allRecords.map(record => (
                        <div
                            key={record.idRecord}
                            className="flex items-center justify-between bg-gray-50 dark:bg-[#1a1a1a] border border-gray-200 dark:border-white/5 rounded-xl px-5 py-3.5"
                        >
                            <div className="flex items-center gap-3">
                                <span
                                    className="px-3 py-1.5 rounded-lg text-xs font-black shadow-sm flex-shrink-0"
                                    style={{ backgroundColor: getLevelColor(record.levelVar), color: '#000' }}
                                >
                                    {getLevelLabel(record.levelVar)}
                                </span>
                                <div>
                                    <p className="text-sm font-bold text-gray-900 dark:text-white">
                                        {record.firstName} {record.lastName}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                        {new Date(record.createdAt).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' })}
                                        {' — '}
                                        <span className="font-bold text-teal-600 dark:text-teal-400">
                                            {parseFloat(record.totalHours).toFixed(2)}h
                                        </span>
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => handleViewRecord(record.idRecord)}
                                className="text-xs font-bold text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 border border-teal-200 dark:border-teal-500/30 rounded-lg px-4 py-2 hover:bg-teal-50 dark:hover:bg-teal-500/10 transition-colors"
                            >
                                {t.view}
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    // ─── Modal mode ────────────────────────────────────
    if (isModal) {
        return (
            <div
                className="fixed inset-0 z-[110] flex items-center justify-center p-4 md:p-6 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300"
                onClick={onClose}
            >
                <div
                    className="bg-white dark:bg-gray-900 rounded-3xl max-w-5xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-200 dark:border-gray-800 animate-in zoom-in-95 duration-200 custom-scrollbar"
                    onClick={e => e.stopPropagation()}
                >
                    <div className="sticky top-0 z-10 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 rounded-t-3xl px-8 py-5 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl">
                                <ClockIcon className="w-5 h-5 text-white" />
                            </div>
                            <h2 className="text-xl font-black text-gray-900 dark:text-white">{t.title}</h2>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition"
                        >
                            <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <div className="p-6 md:p-8">
                        {historySection}
                        {generateSection}
                        {overviewSection}
                    </div>
                </div>
            </div>
        );
    }

    // ─── Page mode (standalone) ────────────────────────
    return (
        <div className="pb-12">
            {/* Header Banner */}
            <div className="relative overflow-hidden rounded-[2rem] p-10 h-64 bg-gradient-to-br from-[#006064] via-[#00838f] to-[#01579b] border border-white/5 !mb-12 shadow-xl">
                <div className="absolute inset-0 opacity-20 pointer-events-none"
                    style={{ backgroundImage: 'linear-gradient(90deg, transparent 50%, rgba(0,0,0,0.5) 50%)', backgroundSize: '4px 100%' }} />

                <div className="relative z-10 h-full flex flex-col justify-between">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-teal-600 rounded-2xl shadow-lg shadow-teal-500/20">
                            <ClockIcon className="w-6 h-6 text-white" />
                        </div>
                        <h1 className="text-4xl font-black text-white tracking-tight">{t.title}</h1>
                    </div>
                    <p className="text-white/60 text-lg max-w-md leading-snug">{t.subtitle}</p>
                </div>

                <div className="absolute right-[-40px] bottom-[-40px] opacity-20">
                    <ClockIcon className="w-80 h-80 text-teal-500 stroke-[0.5]" />
                </div>
            </div>

            {/* Global history */}
            {historySection}

            {/* Generate form section */}
            {generateSection}

            {/* Overview: all levels with hours */}
            {overviewSection}
        </div>
    );
};

export default HoursRecord;
